import type { OperatorRole } from './auth';

export const copy = {
  brand: {
    title: 'PrismaHub',
    subtitle: 'Панель управления',
  },
  nav: {
    dashboard: 'Обзор',
    users: 'Пользователи',
    agents: 'Агенты',
    workspaces: 'Workspaces',
    billing: 'Платежи',
    usage: 'Расход AI',
    platform: 'Платформа',
    instagram: 'Instagram',
    telegram: 'Telegram Business',
    audit: 'Журнал',
    system: 'Состояние',
    models: 'Модели AI',
    modelsApp: 'Модели AI · App',
    modelsStudio: 'Модели AI · Studio',
    plans: 'Тарифы',
    team: 'Команда',
    sections: {
      operations: 'Операции',
      product: 'Продукт',
      analytics: 'Аналитика',
      integrations: 'Интеграции',
      system: 'Система',
    },
  },
  common: {
    loading: 'Загрузка...',
    retry: 'Повторить',
    yes: 'Да',
    no: 'Нет',
    previous: 'Назад',
    next: 'Далее',
    search: 'Найти',
    logout: 'Выйти',
    show: 'Показать',
    hide: 'Скрыть',
    notAvailable: 'Нет данных',
    openProfile: 'Открыть профиль',
    userFallback: 'Пользователь',
  },
  pagination: {
    page: (n: number) => `Страница ${n}`,
    items: (count: number) => `${count} записей`,
    range: (start: number, end: number, total: number) => `${start}-${end} из ${total}`,
    empty: 'Записей нет',
  },
  login: {
    title: 'PrismaHub',
    subtitle: 'Вход для сотрудников',
    email: 'Электронная почта',
    password: 'Пароль',
    submit: 'Войти',
    submitting: 'Вход...',
    defaultError: 'Не удалось войти. Проверьте почту и пароль.',
  },
  topBar: {
    session: 'Текущая сессия',
  },
  errors: {
    loadDashboard: 'Не удалось загрузить обзор.',
    loadUsers: 'Не удалось загрузить список пользователей.',
    loadUser: 'Не удалось загрузить карточку пользователя.',
    loadAgents: 'Не удалось загрузить список агентов.',
    loadPayments: 'Не удалось загрузить список платежей.',
    loadUsage: 'Не удалось загрузить аналитику расходов AI.',
    loadAudit: 'Не удалось загрузить журнал.',
    loadFlags: 'Не удалось загрузить настройки платформы.',
    loadEnforcement: 'Не удалось загрузить метрики enforcement.',
    loadMigration: 'Не удалось загрузить статус перехода на вход по почте.',
    loadSystem: 'Не удалось загрузить состояние системы.',
    loadModels: 'Не удалось загрузить список моделей.',
    loadProviders: 'Не удалось загрузить настройки провайдеров.',
    loadStudioModel: 'Не удалось загрузить настройки Studio.',
    updateModel: 'Не удалось обновить настройки модели.',
    updateStudioModel: 'Не удалось сохранить настройки Studio.',
    updateProvider: 'Не удалось сохранить ключ провайдера.',
    resyncModels: 'Не удалось синхронизировать модели.',
    loadInstagramSetup: 'Не удалось загрузить настройки Instagram.',
    loadInstagramStatus: 'Не удалось загрузить статус Instagram bot.',
    grantCredits: 'Не удалось начислить кредиты.',
    assignPlan: 'Не удалось сменить тариф.',
    deleteUser: 'Не удалось удалить пользователя.',
    impersonate: 'Не удалось войти под пользователем.',
    reconcile: 'Не удалось сверить платёж.',
    insufficientRole: 'Недостаточно прав для этого раздела.',
    loadAgent: 'Не удалось загрузить агента.',
    loadPayment: 'Не удалось загрузить платёж.',
    loadWorkspaces: 'Не удалось загрузить workspaces.',
    loadPlans: 'Не удалось загрузить тарифы.',
    loadTeam: 'Не удалось загрузить команду.',
    loadTelegram: 'Не удалось загрузить Telegram ботов.',
    updateAgent: 'Не удалось обновить агента.',
    deleteMemory: 'Не удалось удалить факт памяти.',
    revokeSessions: 'Не удалось отозвать сессии.',
    updateUser: 'Не удалось обновить пользователя.',
    exportCsv: 'Не удалось экспортировать CSV.',
    generic: 'Не удалось выполнить действие. Попробуйте ещё раз.',
    grantAmount: 'Укажите целое положительное число.',
    grantReason: 'Причина должна содержать не менее 3 символов.',
    assignPlanWorkspace: 'Не найден personal workspace пользователя. Обновите карточку и повторите.',
    assignPlanOption: 'Тарифы ещё не загружены. Обновите карточку и повторите.',
  },
  dashboard: {
    title: 'Обзор',
    uptime: (value: string) => `Сервер работает: ${value}`,
    users: 'Пользователей',
    agents: 'Агентов',
    mrr: 'Доход в месяц',
    creditsToday: 'Кредитов потрачено сегодня',
    migrationTitle: 'Способы входа',
    migrationTelegram: 'Только Telegram',
    migrationEmail: 'Только почта',
    migrationBoth: 'Почта и Telegram',
    migrationEmpty: 'Данных о способах входа пока нет.',
    migrationReady: 'Готовность к отключению Telegram-регистрации',
    authTitle: 'Входы и регистрация',
    authEmpty: 'Показателей входа пока нет.',
    newUsers7d: 'Новых за 7 дней',
    newUsers30d: 'Новых за 30 дней',
    failedPayments: 'Ошибок оплаты за 24 ч',
    migrationLink: 'Подробнее о миграции',
  },
  platform: {
    title: 'Платформа',
    description: 'Текущие настройки входа, оплаты и памяти.',
    flagsTitle: 'Настройки',
    memoryFlagsTitle: 'Память и контекст',
    memoryFlagsDescription:
      'Включение и отключение функций памяти для тестирования. Изменения применяются сразу, без перезапуска сервера.',
    memorySummary: 'Содержание длинных диалогов',
    memorySummaryHint: 'Автосжатие истории чата для Pro и выше. Списывает кредиты за генерацию.',
    memorySemantic: 'Семантический поиск по истории',
    memorySemanticHint: 'Embeddings сообщений и поиск по смыслу. Только Business и выше.',
    memoryEnvDefault: 'Значение по умолчанию из env',
    memoryReadOnly: 'Только просмотр. Изменять могут главные администраторы.',
    memoryMetricsTitle: 'Метрики памяти (runtime)',
    memoryMetricsHits: 'Попадания памяти в промпт',
    memoryMetricsHitRate: 'Доля диалогов с фактами',
    memoryMetricsSummary: 'Средняя длина summary',
    memoryMetricsExtractions: 'Автоизвлечения',
    memoryMetricsAbSkips: 'A/B control (без памяти)',
    migrationTitle: 'Переход на вход по почте',
    deploymentNote:
      'Настройки входа и оплаты задаются при развёртывании. Изменить их можно только через обновление сервера.',
    telegramOnly: 'Только Telegram',
    emailOnly: 'Только почта',
    emailAndTelegram: 'Почта и Telegram',
    readyForCutoff: 'Готовность к отключению Telegram-регистрации',
    enforcementTitle: 'Enforcement',
    enforcementCounters: 'Счётчики enforcement',
  },
  models: {
    title: 'Модели AI',
    titleApp: 'Модели AI · App',
    titleStudio: 'Модели AI · Studio',
    description: 'Провайдеры маршрутизации, API-слаги, цены COGS (вход/выход) и переключатели доступности.',
    descriptionApp:
      'Полный каталог моделей OpenRouter. Включите Prisma App для чата, агентов и памяти.',
    descriptionStudio:
      'Полный каталог моделей OpenRouter. Включите Prisma Studio для генерации в композере.',
    model: 'Модель',
    category: 'Тип',
    tier: 'Тариф',
    active: 'В каталоге',
    route: 'Маршрут',
    openrouter: 'OpenRouter',
    apiSlug: 'API-слаг',
    enabled: 'Включена',
    unavailable: 'Недоступна',
    readOnly: 'Только просмотр. Изменять могут главные администраторы.',
    openrouterNotConfigured: 'Ключ OpenRouter не настроен.',
    providersTitle: 'API-ключи провайдеров',
    providersDescription:
      'Добавьте ключ OpenRouter. После сохранения нажмите «Синхронизировать модели», чтобы подтянуть полный каталог.',
    providerConfigured: 'Настроен',
    providerMissing: 'Не настроен',
    providerSource: (source: string) =>
      source === 'db' ? 'Источник: база' : source === 'env' ? 'Источник: .env' : 'Источник: не задан',
    providerBaseUrl: 'Base URL',
    providerApiKey: 'API-ключ',
    providerKeyPlaceholder: 'Вставьте ключ',
    providerKeyPlaceholderSet: 'Оставьте пустым, чтобы не менять',
    providerSave: 'Сохранить',
    providerSaved: 'Ключ провайдера сохранён',
    resync: 'Синхронизировать модели',
    resyncing: 'Синхронизация...',
    resyncDone: (count: number) => `Синхронизировано записей: ${count}`,
    filterProvider: 'Провайдер',
    filterProviderAny: 'Любой',
    filterInApp: 'Только в App',
    filterInStudio: 'Только в Studio',
    filterUnassigned: 'Не назначены',
    filterActive: 'Активные',
    filterInactive: 'Неактивные',
    columnApp: 'Prisma App',
    columnStudio: 'Prisma Studio',
    providerOpenrouter: 'OpenRouter',
    providerNone: 'Отключена',
    searchPlaceholder: 'Поиск по названию или ID',
    filterAll: 'Все',
    filterText: 'Текст',
    filterCode: 'Код',
    filterImage: 'Изображения',
    filterVideo: 'Видео',
    filterAudio: 'Аудио',
    filterEmbedding: 'Эмбеддинги',
    filterLipsync: 'Lip sync',
    filterTts: 'TTS',
    studioKindImage: 'Изображения',
    studioKindVideo: 'Видео',
    studioKindLipsync: 'Lip sync',
    studioKindTts: 'TTS',
    routingNote: 'Маршрут',
    routingLocal: 'Локальный pipeline',
    providerUnavailable: 'Провайдеры недоступны',
    priceIn: 'Вход',
    priceOut: 'Выход',
    pricePerMtok: '₽/M токенов',
    pricePerImage: '₽/изображение',
    priceUnknown: '—',
    filteredCount: (shown: number, total: number) => `Показано ${shown} из ${total}`,
    refreshing: 'обновление',
    editStudio: 'Настроить Studio',
    studioEditorTitle: 'Настройки Studio',
    studioEditorDescription: 'Описание, тариф, параметры композера и кредиты для выбранной модели.',
    studioEditorBack: 'К списку моделей',
    studioEditorSave: 'Сохранить',
    studioEditorSaved: 'Настройки Studio сохранены',
    studioEditorLoadError: 'Не удалось загрузить настройки Studio.',
    studioEditorSaveError: 'Не удалось сохранить настройки Studio.',
    studioEditorNotCatalog: 'Модель не входит в Studio creative-каталог.',
    studioSectionBasic: 'Основное',
    studioSectionPlan: 'Тариф и доступ',
    studioSectionCapabilities: 'Параметры композера',
    studioSectionCredits: 'Кредиты',
    studioSectionDefaults: 'Значения по умолчанию',
    studioSectionPreview: 'Итоговая конфигурация',
    studioFieldDisplayName: 'Название',
    studioFieldDescription: 'Описание',
    studioFieldSortOrder: 'Порядок в списке',
    studioFieldFeatured: 'Featured',
    studioFieldMinPlan: 'Минимальный тариф',
    studioFieldVideoTier: 'Тип видео',
    studioFieldGeminiPro: 'Требует Gemini Pro Image',
    studioFieldAspectRatios: 'Соотношения сторон',
    studioFieldResolutions: 'Разрешения',
    studioFieldTiers: 'Качество (tier)',
    studioFieldDurations: 'Длительность (сек)',
    studioFieldMaxVariants: 'Макс. вариантов',
    studioPlanFree: 'Free',
    studioPlanLite: 'Lite',
    studioPlanPro: 'Pro',
    studioPlanMax: 'Max',
    studioVideoTierFast: 'Fast',
    studioVideoTierQuality: 'Quality',
  },
  system: {
    title: 'Состояние',
    description: 'Работоспособность сервера, скорость ответов и готовность входа.',
    status: 'Статус',
    uptime: 'Время работы',
    perfTitle: 'Производительность',
    authTitle: 'Готовность входа',
    chats: 'Обработано чатов',
    streams: 'Потоковых ответов',
    errors: 'Ошибок',
    serviceUptime: 'Время работы сервиса',
    latency: 'Скорость ответов',
  },
  instagram: {
    title: 'Инстаграм автоматизация',
    description: 'Мониторинг и управление Instagram bot для комментариев и Direct.',
    refresh: 'Обновить',
    loadingSetup: 'Проверка подключения...',
    loadingStatus: 'Загрузка статуса бота...',
    notConfiguredTitle: 'Instagram bot не подключён к API',
    notConfiguredBody:
      'Чтобы панель могла управлять ботом, добавьте переменные окружения на сервер PrismaHub API и перезапустите API.',
    tabs: {
      overview: 'Обзор',
      settings: 'Настройки',
      operations: 'Операции',
      setup: 'Подключение',
      privacy: 'Персональные данные',
    },
    accountStatus: 'Статус аккаунта',
    webhookEvents: 'Вебхуки',
    replyJobsDue: 'Ответы в очереди',
    oldestDue: 'Старейшая задача',
    delivery24h: 'Доставка за 24 ч',
    jobsTitle: 'Задачи ответов',
    deliveryTitle: 'Доставка за 24 часа',
    deliveryChannels: {
      public: 'Публичные комментарии',
      private: 'Приватные ответы',
      dm: 'Direct',
    },
    deliverySent: 'Отправлено',
    deliveryFailed: 'Ошибки',
    deliveryBlocked: 'Заблокировано',
    topErrorsTitle: 'Частые ошибки',
    noErrors: 'Ошибок за последние 24 часа нет.',
    reconcilerTitle: 'Reconciler',
    reconcilerStarted: 'Старт',
    reconcilerFinished: 'Финиш',
    reconcilerMedia: 'Медиа',
    reconcilerComments: 'Комментарии',
    reconcilerRecovered: 'Восстановлено',
    reconcilerJobs: 'Создано задач',
    reconcilerErrors: 'Ошибок',
    reconcilerEmpty: 'Reconciler ещё не запускался.',
    connectionTitle: 'Подключение',
    instagramAccount: 'Аккаунт',
    accountId: 'Instagram account ID',
    publicBaseUrl: 'Публичный URL worker',
    graphApiBase: 'Graph API base',
    graphApiVersion: 'Graph API version',
    hasAccessToken: 'Access token задан',
    hasAppSecret: 'App secret задан',
    hasAdminKey: 'Admin API key задан',
    flagsTitle: 'Флаги бота',
    flagBotEnabled: 'Бот включён',
    flagPublicReply: 'Публичные ответы',
    flagPrivateReply: 'Приватные ответы',
    flagDmAutoreply: 'Автоответ в Direct',
    flagBackfillReply: 'Ответы при backfill',
    flagReconciler: 'Reconciler',
    deploymentNote:
      'Эти флаги и правила ответов задаются в Cloudflare Workers. Изменение требует деплоя ig-bot.',
    rulesTitle: 'Правила ответов',
    noRules: 'Правила не настроены.',
    ruleKeywords: 'Ключевые слова',
    rulePublic: 'Публичный ответ',
    rulePrivate: 'Приватный ответ',
    rulePrivateConfigured: 'настроен',
    ruleAlways: 'Всегда отвечать (без проверки диалога)',
    keywordsNote:
      'Ключевые слова из всех правил также доступны в config.commentKeywords на вкладке обзора через API status.',
    limitsTitle: 'Лимиты и reconciler',
    limitPrivateAge: 'Окно приватного ответа, дней',
    limitBackfillPages: 'Страниц backfill за запуск',
    limitAttempts: 'Попыток отправки',
    limitRate: 'Сообщений в минуту',
    limitReconcilerInterval: 'Интервал reconciler, мин',
    limitReconcilerMedia: 'Медиа за reconciler',
    limitReconcilerPages: 'Страниц комментариев на медиа',
    limitReconcilerLookback: 'Lookback reconciler, ч',
    backfillTitle: 'Backfill комментариев',
    backfillDescription:
      'Загрузить комментарии с поста из Instagram Graph API. Отправка сработает только если включены нужные флаги в worker.',
    mediaIdLabel: 'Media ID поста',
    backfillSend: 'Создавать и отправлять ответы',
    backfillAfter: 'Курсор after (необязательно)',
    backfillMaxPages: 'Макс. страниц (необязательно)',
    backfillSubmit: 'Запустить backfill',
    replyTitle: 'Ручной ответ на комментарий',
    replyDescription:
      'Ответ по comment ID, который уже сохранён в базе бота (через webhook, backfill или reconciler).',
    commentIdLabel: 'Comment ID',
    replyForce: 'Принудительно (force): игнорировать ключевые слова и проверку диалога',
    replySubmit: 'Отправить ответ',
    running: 'Выполняется...',
    backfillSuccess: 'Backfill выполнен.',
    replySuccess: 'Запрос на ответ отправлен.',
    lookupSuccess: 'Поиск выполнен.',
    redactSuccess: 'Данные субъекта обезличены.',
    actionFailed: 'Не удалось выполнить операцию.',
    mediaIdRequired: 'Укажите media ID.',
    commentIdRequired: 'Укажите comment ID.',
    subjectSelectorRequired: 'Укажите хотя бы один идентификатор субъекта данных.',
    redactConfirm: 'Обезличить найденные данные? Это действие необратимо.',
    lastResult: 'Последний результат',
    setupWorkerTitle: 'Worker',
    setupWorkerUrl: 'URL worker',
    setupHealth: 'Health check',
    setupWebhookPath: 'Путь webhook',
    healthOk: 'OK',
    healthBad: 'Ошибка',
    openWebhookUrl: 'Открыть callback URL',
    setupMetaTitle: 'Чеклист Meta',
    setupMetaChecklist: [
      'Instagram Business аккаунт связан со страницей Facebook PrismaHub',
      'В Meta App подписка object=instagram, fields=comments,messages',
      'Callback URL worker сохранён в Meta Webhooks',
      'Page token с правами instagram_manage_comments и instagram_manage_messages',
      'Для production: Advanced Access и App Review',
    ],
    setupNotesTitle: 'Важно',
    setupNotes: [
      'Если вебхуки не приходят, reconciler подхватывает свежие комментарии каждые 10 минут.',
      'Одинаковые шаблоны ответов Instagram может отклонять как спам. Используйте разные формулировки.',
      'Private reply и conversations API требуют полной настройки Instagram Messaging в Meta App.',
    ],
    privacyTitle: 'Субъект данных (GDPR)',
    privacyDescription:
      'Поиск и обезличивание данных по commentId, directMessageId, commenterId или username.',
    lookupSubmit: 'Найти',
    redactSubmit: 'Обезличить',
    privacyReport: 'Отчёт',
    jobStatusLabels: {
      pending: 'Ожидают',
      sending: 'Отправляются',
      sent: 'Отправлены',
      retryable: 'Повтор',
      failed: 'Ошибка',
      blocked: 'Заблокированы',
    },
    errors: {
      loadSetup: 'Не удалось проверить подключение Instagram bot.',
      loadStatus: 'Не удалось загрузить статус Instagram bot.',
    },
  },
  userDetail: {
    back: 'К списку пользователей',
    profile: 'Профиль',
    identities: 'Способы входа',
    billing: 'Оплата',
    impersonate: 'Войти как пользователь',
    account: 'Аккаунт',
    usage: 'Использование',
    plan: 'Тариф',
    username: 'Имя в Telegram',
    telegram: 'Telegram',
    telegramConnected: 'Подключён',
    telegramNotConnected: 'Не подключён',
    language: 'Язык',
    onboarded: 'Онбординг пройден',
    created: 'Дата регистрации',
    creditsBalance: 'Баланс кредитов',
    creditsLimit: 'Лимит кредитов',
    fairUse: 'Справедливое использование',
    agents: 'Агенты',
    payments: 'Платежи',
    provider: 'Способ входа',
    email: 'Почта',
    verified: 'Подтверждён',
    lastUsed: 'Последний вход',
    billingAccount: 'Счёт',
    planCreditsRemaining: 'Кредитов по тарифу',
    bonusCredits: 'Бонусные кредиты',
    noBillingAccount: 'Счёт не создан.',
    subscription: 'Подписка',
    subscriptionPlan: 'Тариф',
    subscriptionStatus: 'Статус',
    periodEnd: 'Оплачено до',
    grantTitle: 'Начислить кредиты',
    grantAmount: 'Количество',
    grantReason: 'Причина',
    grantSubmit: 'Начислить',
    grantSubmitting: 'Начисление...',
    grantSuccess: 'Кредиты начислены.',
    noGrantPermission: 'У вашей роли нет права начислять кредиты.',
    assignPlanTitle: 'Сменить тариф',
    assignPlanSelect: 'Тариф',
    assignPlanMonths: 'Срок подписки, мес.',
    assignPlanReason: 'Причина (для аудита)',
    assignPlanSubmit: 'Применить тариф',
    assignPlanSubmitting: 'Смена тарифа...',
    assignPlanSuccess: 'Тариф обновлён.',
    noAssignPlanPermission: 'У вашей роли нет права менять тариф.',
    deleteUserTitle: 'Удаление аккаунта',
    deleteUserHint:
      'Аккаунт будет стёрт по политике GDPR: агенты, интеграции и персональные данные удаляются. Действие необратимо.',
    deleteUserReason: 'Причина удаления',
    deleteUserConfirm: 'Введите DELETE для подтверждения',
    deleteUserSubmit: 'Удалить пользователя',
    deleteUserSubmitting: 'Удаление...',
    deleteUserSuccess: 'Пользователь удалён.',
    noDeleteUserPermission: 'Удалять пользователей может только главный администратор.',
    sessions: 'Сессии',
    creditsLedger: 'Журнал кредитов',
    legal: 'Юридическое',
    timezone: 'Часовой пояс',
    memberships: 'Участий в workspace',
    usageLink: 'Расход AI пользователя',
    revokeSessions: 'Отозвать все сессии',
    revokeSessionsConfirm: 'Отозвать все активные сессии пользователя?',
    revokeSessionsSuccess: 'Сессии отозваны.',
    userAgentsTitle: 'Агенты пользователя',
    allAgents: 'Все агенты',
    sessionId: 'ID сессии',
    sessionCreated: 'Создана',
    sessionExpires: 'Истекает',
    ledgerType: 'Тип',
    ledgerBucket: 'Корзина',
    ledgerAmount: 'Сумма',
    ledgerBalance: 'Баланс после',
    legalSource: 'Источник',
    legalAccepted: 'Принято',
    legalOffer: 'Оферта',
    legalPrivacy: 'Политика',
    legalConsent: 'Согласие',
    editProfile: 'Редактирование',
    saveProfile: 'Сохранить',
    profileSaved: 'Профиль обновлён.',
  },
  agentDetail: {
    back: 'К списку агентов',
    overview: 'Обзор',
    memory: 'Память',
    owner: 'Владелец',
    model: 'Модель',
    temperature: 'Температура',
    messages: 'Сообщения',
    sources: 'Источники',
    memories: 'Факты памяти',
    memoryEnabled: 'Память включена',
    systemPrompt: 'Системный промпт',
    showPrompt: 'Показать промпт',
    hidePrompt: 'Скрыть промпт',
    editAgent: 'Изменить агента',
    status: 'Статус',
    saveAgent: 'Сохранить',
    agentSaved: 'Агент обновлён.',
    memoryEmpty: 'Фактов памяти пока нет.',
    memoryDeleteConfirm: 'Удалить факт памяти?',
    memoryDeleted: 'Факт памяти удалён.',
    fact: 'Факт',
    source: 'Источник',
    created: 'Создан',
    delete: 'Удалить',
  },
  paymentDetail: {
    back: 'К списку платежей',
    order: 'Заказ',
    user: 'Пользователь',
    amount: 'Сумма',
    status: 'Статус',
    provider: 'Провайдер',
    providerPaymentId: 'ID у провайдера',
    providerStatus: 'Статус провайдера',
    purpose: 'Назначение',
    plan: 'Тариф',
    credits: 'Кредиты',
    created: 'Создан',
    completed: 'Завершён',
    processed: 'Обработан',
    metadata: 'Метаданные',
    reconcile: 'Сверить',
    reconcileSuccess: 'Платёж сверен.',
  },
  workspaces: {
    title: 'Workspaces',
    description: 'Рабочие пространства на платформе.',
    type: 'Тип',
    name: 'Название',
    slug: 'Slug',
    owner: 'Владелец',
    members: 'Участники',
    agents: 'Агенты',
    created: 'Создан',
    empty: 'Workspaces не найдены.',
  },
  plansPage: {
    title: 'Тарифы',
    description: 'Каталог тарифов платформы (только просмотр).',
    id: 'ID',
    name: 'Название',
    lineage: 'Линейка',
    price: 'Цена',
    credits: 'Кредиты',
    fairUse: 'Fair use',
    premiumCredits: 'Премиум кредиты',
    lineageCredits: 'Кредиты',
    lineageUnlimited: 'Безлимит',
  },
  team: {
    title: 'Команда',
    description: 'Операторы панели управления.',
    email: 'Почта',
    name: 'Имя',
    role: 'Роль',
    active: 'Активен',
    lastLogin: 'Последний вход',
    create: 'Добавить оператора',
    resetPassword: 'Сбросить пароль',
    password: 'Пароль',
    createSubmit: 'Создать',
    save: 'Сохранить',
    created: 'Оператор создан.',
    updated: 'Оператор обновлён.',
    passwordReset: 'Пароль обновлён.',
    empty: 'Операторов нет.',
  },
  telegram: {
    title: 'Telegram Business',
    description: 'Диагностика подключённых Telegram ботов.',
    bot: 'Бот',
    status: 'Статус',
    webhook: 'Webhook',
    capability: 'Business mode',
    lastCheck: 'Последняя проверка',
    diagnostics: 'Диагностика',
    refresh: 'Обновить',
    empty: 'Боты не подключены.',
    webhookUrl: 'URL webhook',
    pendingUpdates: 'Ожидающие обновления',
    lastError: 'Последняя ошибка',
  },
  billing: {
    filterStatus: 'Статус',
    filterSearch: 'Номер заказа',
    filterUser: 'User ID',
    allStatuses: 'Все статусы',
    exportCsv: 'Экспорт CSV',
  },
  agents: {
    filterStatus: 'Статус',
    filterUser: 'User ID',
    allStatuses: 'Все статусы',
  },
  users: {
    filterPlan: 'Тариф',
    allPlans: 'Все тарифы',
    exportCsv: 'Экспорт CSV',
  },
  audit: {
    filterAction: 'Действие',
    filterUser: 'User ID',
    metadata: 'Метаданные',
  },
  search: {
    placeholder: 'Поиск пользователей, платежей, агентов...',
    title: 'Быстрый поиск',
    users: 'Пользователи',
    payments: 'Платежи',
    agents: 'Агенты',
    recent: 'Недавние',
    noResults: 'Ничего не найдено.',
    hint: 'Cmd+K или Ctrl+K',
  },
  toast: {
    success: 'Готово',
    error: 'Ошибка',
  },
} as const;

const PLAN_LABELS: Record<string, string> = {
  free: 'Бесплатный',
  start: 'Старт',
  pro: 'Про',
  business: 'Бизнес',
  enterprise: 'Корпоративный',
  personal_unlimited: 'Personal Unlimited',
  creator_unlimited: 'Creator Unlimited',
  pro_unlimited: 'Pro Unlimited',
  business_unlimited: 'Business Unlimited',
};

const ROLE_LABELS: Record<OperatorRole, string> = {
  SUPER_ADMIN: 'Главный администратор',
  READONLY: 'Только просмотр',
  SUPPORT: 'Поддержка',
  BILLING: 'Биллинг',
};

const AGENT_STATUS: Record<string, string> = {
  active: 'Активен',
  paused: 'На паузе',
  archived: 'В архиве',
  draft: 'Черновик',
};

const PAYMENT_STATUS: Record<string, string> = {
  pending: 'Ожидает оплаты',
  processing: 'В обработке',
  completed: 'Оплачен',
  paid: 'Оплачен',
  failed: 'Ошибка',
  cancelled: 'Отменён',
  refunded: 'Возврат',
};

const PROVIDER_LABELS: Record<string, string> = {
  email: 'Почта',
  telegram: 'Telegram',
  google: 'Google',
  apple: 'Apple',
};

const AUDIT_ACTIONS: Record<string, string> = {
  'operator.login': 'Вход в панель',
  'operator.logout': 'Выход из панели',
  'operator.refresh': 'Обновление сессии',
  'user.update': 'Изменение пользователя',
  'user.grant_credits': 'Начисление кредитов',
  'user.assign_plan': 'Смена тарифа',
  'user.delete': 'Удаление пользователя',
  'user.revoke_sessions': 'Сброс сессий пользователя',
  'user.impersonate': 'Вход под пользователем',
  'agent.update': 'Изменение агента',
  'payment.reconcile': 'Сверка платежа',
};

const SECURITY_EVENTS: Record<string, string> = {
  OPERATOR_IMPERSONATION: 'Вход под пользователем',
  LOGIN_SUCCESS: 'Успешный вход',
  LOGIN_FAILED: 'Неудачный вход',
};

const AUTH_METRIC_LABELS: Record<string, string> = {
  email_login_success: 'Вход по почте',
  telegram_login_success: 'Вход через Telegram',
  telegram_signup_blocked: 'Регистрация в Telegram заблокирована',
  telegram_bind_success: 'Привязка Telegram',
  account_merge_success: 'Объединение аккаунтов',
  account_merge_failed: 'Ошибка объединения аккаунтов',
};

const PLATFORM_FLAGS: Record<string, string> = {
  auth_email_first_miniapp: 'Сначала вход по почте в мини-приложении',
  auth_telegram_signup_enabled: 'Регистрация через Telegram',
  auth_auto_bind_telegram: 'Автопривязка Telegram',
  auth_telegram_signup_cutoff: 'Дата отключения регистрации в Telegram',
  browser_billing_enabled: 'Оплата в браузере',
  payment_provider: 'Платёжный провайдер',
};

const SUBSCRIPTION_STATUS: Record<string, string> = {
  active: 'Активна',
  cancelled: 'Отменена',
  past_due: 'Просрочена',
  trialing: 'Пробный период',
  paused: 'На паузе',
};

const LANGUAGE_LABELS: Record<string, string> = {
  ru: 'Русский',
  en: 'Английский',
};

const API_ERROR_MAP: Record<string, string> = {
  'Invalid credentials': 'Неверная почта или пароль',
  'Session expired': 'Сессия истекла. Войдите снова',
  'Invalid JSON response': copy.errors.generic,
  'Operator session required': 'Требуется вход в панель',
  'Insufficient operator role': 'Недостаточно прав для этого действия',
};

export function translateApiError(message: string): string {
  return API_ERROR_MAP[message] ?? copy.errors.generic;
}

export function formatPlan(plan: string): string {
  return PLAN_LABELS[plan.toLowerCase()] ?? 'Тариф';
}

export function formatRole(role: string): string {
  return ROLE_LABELS[role as OperatorRole] ?? 'Сотрудник';
}

export function formatAgentStatus(status: string): string {
  return AGENT_STATUS[status.toLowerCase()] ?? 'Неизвестно';
}

export function formatPaymentStatus(status: string): string {
  return PAYMENT_STATUS[status.toLowerCase()] ?? 'Статус неизвестен';
}

export function formatProvider(provider: string): string {
  return PROVIDER_LABELS[provider.toLowerCase()] ?? 'Способ входа';
}

export function formatAuditAction(action: string): string {
  return AUDIT_ACTIONS[action] ?? 'Действие в системе';
}

export function formatSecurityEvent(eventType: string): string {
  return SECURITY_EVENTS[eventType] ?? 'Событие безопасности';
}

export function formatAuditTarget(type: string | null): string {
  if (!type) return copy.common.notAvailable;
  const map: Record<string, string> = {
    user: 'Пользователь',
    agent: 'Агент',
    payment: 'Платёж',
  };
  return map[type] ?? 'Объект';
}

export function formatYesNo(value: boolean): string {
  return value ? copy.common.yes : copy.common.no;
}

export function formatHealthStatus(status: string): string {
  if (status === 'ok') return 'Всё в порядке';
  if (status === 'degraded') return 'Есть замечания';
  return 'Требует внимания';
}

export function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds} сек`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const restMin = minutes % 60;
  if (hours < 24) return restMin > 0 ? `${hours} ч ${restMin} мин` : `${hours} ч`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours > 0 ? `${days} д ${restHours} ч` : `${days} д`;
}

export function formatAuthMetricLabel(key: string): string {
  return AUTH_METRIC_LABELS[key] ?? 'Показатель входа';
}

export function formatPlatformFlagKey(key: string): string {
  return PLATFORM_FLAGS[key] ?? 'Настройка';
}

export function formatPlatformFlagValue(value: boolean | string): string {
  if (typeof value === 'boolean') return formatYesNo(value);
  if (!value) return copy.common.notAvailable;
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return new Date(value).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }
  if (value === 'ozon_pay') return 'Ozon Pay';
  return 'Настроено';
}

export function formatUserSubtitle(username: string | null | undefined): string | null {
  if (username) return `@${username}`;
  return null;
}

export function formatDisplayName(
  name: string | null | undefined,
  fallback = copy.common.userFallback,
): string {
  const trimmed = name?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
}

export function formatModelName(model: string | null | undefined): string {
  if (!model) return copy.common.notAvailable;
  return 'Нейросеть';
}

export function formatReadinessCheck(key: string): string {
  const map: Record<string, string> = {
    feature_flags: 'Настройки входа',
    email_otp: 'Письма с кодом',
    telegram_bind: 'Привязка Telegram',
    bind_init_data: 'Проверка Telegram',
    account_merge: 'Объединение аккаунтов',
    auth_config: 'Конфигурация входа',
  };
  return map[key] ?? 'Проверка';
}

export function formatSubscriptionStatus(status: string): string {
  return SUBSCRIPTION_STATUS[status.toLowerCase()] ?? 'Подписка';
}

export function formatLanguage(language: string): string {
  return LANGUAGE_LABELS[language.toLowerCase()] ?? 'Не указан';
}

export function formatReadinessValue(ok: boolean, reason?: string): string {
  if (ok) return copy.common.yes;
  if (reason === 'EMAIL_PROVIDER_NOT_CONFIGURED') return 'Почта не настроена';
  if (reason === 'BOT_TOKEN_NOT_CONFIGURED') return 'Telegram не настроен';
  return copy.common.no;
}

export function formatMetricLatency(summary: { avg: number; p95: number } | null | undefined): string {
  if (!summary) return copy.common.notAvailable;
  return `среднее ${summary.avg} мс, 95%: ${summary.p95} мс`;
}

export function formatPerfMetricKey(key: string): string {
  const map: Record<string, string> = {
    chat_total_ms: 'Время ответа в чате',
    chat_prepare_ms: 'Подготовка ответа',
    chat_llm_ms: 'Генерация ответа',
    chat_stream_total_ms: 'Время потокового ответа',
    chat_stream_ttft_ms: 'Время до первого слова',
  };
  return map[key] ?? 'Показатель скорости';
}
